/**
 * Simple and safe localStorage wrapper with get, set, and clear functionality
 */
const storageService = {
  /**
   * Check if localStorage is available
   * @returns {boolean} True if localStorage is available
   */
  isAvailable() {
    if (typeof window === "undefined") return false;

    try {
      // Test if localStorage is accessible
      const testKey = "__storage_test__";
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Get item from localStorage
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} Retrieved value or defaultValue
   */
  get(key, defaultValue = null) {
    if (!this.isAvailable()) return defaultValue;

    try {
      const item = window.localStorage.getItem(key);
      if (item === null) return defaultValue;

      // Try to parse JSON, but return the raw string if parsing fails
      try {
        return JSON.parse(item);
      } catch (e) {
        return item;
      }
    } catch (e) {
      console.warn(`Failed to get item '${key}' from localStorage:`, e);
      return defaultValue;
    }
  },

  /**
   * Set item in localStorage
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   * @returns {boolean} True if operation successful
   */
  set(key, value) {
    if (!this.isAvailable()) return false;

    try {
      // Convert to string if not already a string
      const valueToStore =
        typeof value === "string" ? value : JSON.stringify(value);

      window.localStorage.setItem(key, valueToStore);
      return true;
    } catch (e) {
      console.warn(`Failed to set item '${key}' in localStorage:`, e);
      return false;
    }
  },

  /**
   * Remove a specific item from localStorage
   * @param {string} key - Storage key to remove
   * @returns {boolean} True if operation successful
   */
  removeItem(key) {
    if (!this.isAvailable()) return false;

    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`Failed to remove item '${key}' from localStorage:`, e);
      return false;
    }
  },

  /**
   * Clear all items from localStorage
   * @returns {boolean} True if operation successful
   */
  clear() {
    if (!this.isAvailable()) return false;

    try {
      window.localStorage.clear();
      return true;
    } catch (e) {
      console.warn("Failed to clear localStorage:", e);
      return false;
    }
  },
};

export default storageService;
